/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useCallback } from 'preact/hooks';
import { Link } from '#UNSAFE_Link';
import { useFocusWithin } from '#hooks/UNSAFE_useFocusWithin';
import { useHover } from '#hooks/UNSAFE_useHover';
import { classNames } from '#utils/UNSAFE_classNames';
import { dropdownStyles } from './themes/dropdownStyles.css';

type Props = {
  /**
   * Child content of the item.
   */
  children: string;
  /**
   * Specifies whether to highlight the item.
   */
  isHighlighted?: boolean;
  /**
   * Callback invoked when the user triggers the link, for example by clicking or pressing Enter.
   */
  onAction?: () => void;
};

/**
 * This component renders a link that looks like a list item in a Select* component dropdown.
 * It fires an action when the user triggers it, and is intended to be used for launching
 * external application UI from within the dropdown, for example to provide advanced search or
 * add to list functionality.
 * The LinkItem would typically be shown in a fixed position above or below the list itself.
 */
const LinkItem = ({ children, isHighlighted, onAction }: Props) => {
  const { isHover, hoverProps } = useHover();
  const { isFocused, focusProps } = useFocusWithin();

  const handleClick = useCallback(
    (e: Event) => {
      // prevent the link from actually navigating by preventing default and returning false
      e.preventDefault();
      onAction?.();
      return false;
    },
    [onAction]
  );

  const rootClasses = classNames([isFocused ? dropdownStyles.results.linkItemFocus : undefined]);

  const linkClasses = classNames([
    dropdownStyles.results.extraItem,
    isHover || isFocused || isHighlighted ? dropdownStyles.results.linkItemHighlight : undefined
  ]);

  return (
    <div class={rootClasses} {...focusProps}>
      <Link href="#" onClick={handleClick}>
        <div class={linkClasses} {...hoverProps}>
          {children}
        </div>
      </Link>
    </div>
  );
};

export { LinkItem };
